import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Headset, Search, Loader2, Send, StickyNote, ShoppingBag, LifeBuoy, CheckCircle2, RotateCcw, UserCheck, Mail, Phone, Globe2, FolderKanban, ChevronLeft, Inbox } from "lucide-react";
import { toast } from "sonner";
import portal from "@/lib/portalApi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TX = {
  en: {
    title: "Support & sales", sub: "Conversations from the website chat and the customer area. Replies are shown in the chat and emailed to the visitor.",
    active: "Active", open: "Waiting for us", pending: "Waiting for visitor", closed: "Closed", all: "All", allDepts: "All teams",
    sales: "Sales", support: "Support", mine: "Mine", search: "Search name, email or subject", empty: "No conversations", pick: "Select a conversation",
    reply: "Reply", note: "Internal note", replyPh: "Write a reply — the visitor sees it in the chat and by email", notePh: "Only the team sees notes",
    send: "Send", addNote: "Add note", close: "Close", reopen: "Reopen", assign: "Assigned to", nobody: "Nobody", takeIt: "Take it",
    team: "Team", from: "Started on", customer: "Customer account", cases: "Cases", noAccount: "No customer account with this email",
    unread: "unread", openCount: "open", unassigned: "unassigned", mineCount: "mine", system: "Automatic message", shortcut: "Enter to send · Shift + Enter for a new line",
  },
  pt: {
    title: "Apoio e comercial", sub: "Conversas do chat do site e da área de cliente. As respostas aparecem no chat e são enviadas por email ao visitante.",
    active: "Ativas", open: "À nossa espera", pending: "À espera do visitante", closed: "Fechadas", all: "Todas", allDepts: "Todas as equipas",
    sales: "Comercial", support: "Apoio", mine: "Minhas", search: "Pesquisar nome, email ou assunto", empty: "Sem conversas", pick: "Selecione uma conversa",
    reply: "Responder", note: "Nota interna", replyPh: "Escreva uma resposta — o visitante vê-a no chat e por email", notePh: "Só a equipa vê as notas",
    send: "Enviar", addNote: "Adicionar nota", close: "Fechar", reopen: "Reabrir", assign: "Atribuída a", nobody: "Ninguém", takeIt: "Assumir",
    team: "Equipa", from: "Iniciada em", customer: "Conta de cliente", cases: "Processos", noAccount: "Sem conta de cliente com este email",
    unread: "por ler", openCount: "abertas", unassigned: "sem responsável", mineCount: "minhas", system: "Mensagem automática", shortcut: "Enter para enviar · Shift + Enter para nova linha",
  },
  fr: {
    title: "Assistance et ventes", sub: "Conversations du chat du site et de l'espace client. Les réponses s'affichent dans le chat et sont envoyées par email.",
    active: "Actives", open: "En attente de nous", pending: "En attente du visiteur", closed: "Fermées", all: "Toutes", allDepts: "Toutes les équipes",
    sales: "Commercial", support: "Assistance", mine: "Les miennes", search: "Rechercher nom, email ou sujet", empty: "Aucune conversation", pick: "Sélectionnez une conversation",
    reply: "Répondre", note: "Note interne", replyPh: "Écrivez une réponse — le visiteur la voit dans le chat et par email", notePh: "Seule l'équipe voit les notes",
    send: "Envoyer", addNote: "Ajouter la note", close: "Fermer", reopen: "Rouvrir", assign: "Assignée à", nobody: "Personne", takeIt: "Prendre",
    team: "Équipe", from: "Démarrée sur", customer: "Compte client", cases: "Dossiers", noAccount: "Aucun compte client avec cet email",
    unread: "non lues", openCount: "ouvertes", unassigned: "non assignées", mineCount: "les miennes", system: "Message automatique", shortcut: "Entrée pour envoyer · Maj + Entrée pour aller à la ligne",
  },
};

const err = (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Error");
const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString();
};
const STATUS_TONE = { open: "bg-amber-400/15 text-amber-300", pending: "bg-sky-400/15 text-sky-300", closed: "bg-slate-500/15 text-slate-400" };

const DeptChip = ({ d, tx }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${d === "sales" ? "bg-venka-blue/15 text-venka-blue" : "bg-venka-green/15 text-venka-green"}`}>
    {d === "sales" ? <ShoppingBag className="h-3 w-3" /> : <LifeBuoy className="h-3 w-3" />}{tx[d]}
  </span>
);

export const PortalSupport = () => {
  const { i18n } = useTranslation();
  const tx = TX[(i18n.language || "en").slice(0, 2)] || TX.en;
  const { user, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const cid = params.get("c");
  const [filters, setFilters] = useState({ status: "active", department: "", mine: false, q: "" });
  const [list, setList] = useState(null);
  const [summary, setSummary] = useState({});
  const [detail, setDetail] = useState(null);
  const [team, setTeam] = useState([]);
  const [mode, setMode] = useState("reply");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const p = { status: filters.status === "all" ? undefined : filters.status, department: filters.department || undefined, mine: filters.mine || undefined, q: filters.q || undefined };
      const [{ data }, s] = await Promise.all([portal.get("/support/conversations", { params: p }), portal.get("/support/summary")]);
      setList(data); setSummary(s.data);
    } catch (e) { /* ignore while polling */ }
  }, [filters]);
  const loadDetail = useCallback(async () => {
    if (!cid) { setDetail(null); return; }
    try { const { data } = await portal.get(`/support/conversations/${cid}`); setDetail(data); } catch (e) { if (e?.response?.status === 404) setParams({}); }
  }, [cid, setParams]);

  useEffect(() => { loadList(); const iv = setInterval(loadList, 10000); return () => clearInterval(iv); }, [loadList]);
  useEffect(() => { loadDetail(); const iv = setInterval(loadDetail, 5000); return () => clearInterval(iv); }, [loadDetail]);
  useEffect(() => { portal.get("/support/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);
  const nMsgs = detail?.messages?.length || 0;
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [nMsgs, cid]);

  const select = (id) => { setDetail(null); setText(""); setMode("reply"); setParams({ c: id }); };
  const send = async () => {
    const body = text.trim();
    if (!body || !cid) return;
    setBusy(true);
    try {
      await portal.post(`/support/conversations/${cid}/messages`, { body, internal: mode === "note" });
      setText(""); await loadDetail(); loadList();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const patch = async (body) => {
    try { await portal.patch(`/support/conversations/${cid}`, body); await loadDetail(); loadList(); } catch (e) { err(e); }
  };

  const conv = detail?.conversation;
  const pill = (active, onClick, label, testid) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${active ? "bg-venka-blue text-white" : "text-slate-400 hover:bg-slate-800/50"}`}>{label}</button>
  );

  return (
    <div className="space-y-5" data-testid="portal-support">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl md:text-3xl font-bold text-slate-100"><Headset className="h-7 w-7 text-venka-blue" />{tx.title}</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">{tx.sub}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {[["unread", "bg-venka-green/15 text-venka-green"], ["open", "bg-slate-800/60 text-slate-300"], ["unassigned", "bg-amber-400/15 text-amber-300"], ["mine", "bg-venka-blue/15 text-venka-blue"]].map(([k, cls]) => (
            <span key={k} className={`rounded-full px-3 py-1 font-medium ${cls}`}><b className="tabular-nums">{summary[k] ?? "–"}</b> {tx[k === "open" ? "openCount" : k === "mine" ? "mineCount" : k]}</span>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[360px_1fr] lg:h-[calc(100vh-13rem)] lg:min-h-[520px]">
        {/* list */}
        <div className={`flex min-h-0 flex-col rounded-2xl border border-slate-800 bg-slate-900/40 ${cid ? "hidden lg:flex" : "flex"}`}>
          <div className="space-y-2 border-b border-slate-800 p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input className="h-9 bg-slate-950 border-slate-700 pl-9 text-sm" placeholder={tx.search} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="support-search" />
            </div>
            <div className="flex gap-1 overflow-x-auto">
              {["active", "open", "pending", "closed", "all"].map((k) => pill(filters.status === k, () => setFilters({ ...filters, status: k }), tx[k], `support-status-${k}`))}
            </div>
            <div className="flex gap-1 overflow-x-auto">
              {pill(!filters.department, () => setFilters({ ...filters, department: "" }), tx.allDepts)}
              {pill(filters.department === "sales", () => setFilters({ ...filters, department: "sales" }), tx.sales, "support-dept-sales")}
              {pill(filters.department === "support", () => setFilters({ ...filters, department: "support" }), tx.support, "support-dept-support")}
              {pill(filters.mine, () => setFilters({ ...filters, mine: !filters.mine }), tx.mine, "support-mine")}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="support-list">
            {list === null && <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-venka-blue" /></div>}
            {list && list.length === 0 && <div className="flex flex-col items-center gap-2 py-16 text-sm text-slate-500"><Inbox className="h-8 w-8" />{tx.empty}</div>}
            {(list || []).map((c) => (
              <button key={c.id} type="button" onClick={() => select(c.id)} data-testid="support-item"
                className={`block w-full border-b border-slate-800/70 px-4 py-3 text-left transition-colors ${c.id === cid ? "bg-venka-blue/10" : "hover:bg-slate-800/30"}`}>
                <div className="flex items-center gap-2">
                  {c.unread_staff > 0 && <span className="h-2 w-2 shrink-0 rounded-full bg-venka-green" />}
                  <span className={`truncate text-sm ${c.unread_staff > 0 ? "font-bold text-slate-100" : "font-medium text-slate-200"}`}>{c.name}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-slate-500">{when(c.last_message_at)}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-slate-400">{c.subject}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <DeptChip d={c.department} tx={tx} />
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[c.status]}`}>{tx[c.status]}</span>
                  {c.assigned_name && <span className="truncate text-[11px] text-slate-500">· {c.assigned_name}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* conversation */}
        <div className={`min-h-[560px] min-w-0 rounded-2xl border border-slate-800 bg-slate-900/40 lg:min-h-0 ${cid ? "flex" : "hidden lg:flex"} flex-col`}>
          {!cid && <div className="m-auto flex flex-col items-center gap-2 text-sm text-slate-500"><Headset className="h-10 w-10" />{tx.pick}</div>}
          {cid && !conv && <div className="m-auto"><Loader2 className="h-6 w-6 animate-spin text-venka-blue" /></div>}
          {conv && (
            <div className="grid min-h-0 flex-1 xl:grid-cols-[1fr_260px]">
              <div className="flex min-h-0 min-w-0 flex-col">
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 px-4 py-3">
                  <button type="button" className="rounded-full p-1 text-slate-400 lg:hidden" onClick={() => setParams({})}><ChevronLeft className="h-5 w-5" /></button>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-100" data-testid="support-conv-name">{conv.name}</p>
                    <p className="truncate text-xs text-slate-400">{conv.email}</p>
                  </div>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <select className="h-8 rounded-lg border border-slate-700 bg-slate-950 px-2 text-xs" value={conv.department} onChange={(e) => patch({ department: e.target.value })} data-testid="support-dept">
                      <option value="sales">{tx.sales}</option><option value="support">{tx.support}</option>
                    </select>
                    {isAdmin ? (
                      <select className="h-8 max-w-[10rem] rounded-lg border border-slate-700 bg-slate-950 px-2 text-xs" value={conv.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} data-testid="support-assign">
                        <option value="">{tx.assign}: {tx.nobody}</option>
                        {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    ) : conv.assigned_to !== user?.id && (
                      <Button size="sm" variant="outline" className="h-8 rounded-lg border-slate-700 text-xs" onClick={() => patch({ assigned_to: "me" })}><UserCheck className="h-3.5 w-3.5 mr-1" />{tx.takeIt}</Button>
                    )}
                    {conv.status !== "closed" ? (
                      <Button size="sm" className="h-8 rounded-lg bg-venka-green text-xs text-white hover:bg-venka-green/90" onClick={() => patch({ status: "closed" })} data-testid="support-close"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{tx.close}</Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-8 rounded-lg border-slate-700 text-xs" onClick={() => patch({ status: "open" })}><RotateCcw className="h-3.5 w-3.5 mr-1" />{tx.reopen}</Button>
                    )}
                  </div>
                </div>

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="support-thread">
                  {detail.messages.map((m) => {
                    if (m.from === "system") return <p key={m.id} className="mx-auto max-w-md rounded-xl bg-slate-800/40 px-3 py-1.5 text-center text-[11px] text-slate-400" title={tx.system}>{m.body}</p>;
                    if (m.from === "note") return (
                      <div key={m.id} className="mx-auto max-w-lg rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                        <span className="mr-1 inline-flex items-center gap-1 font-semibold"><StickyNote className="h-3 w-3" />{m.author_name}</span>{m.body}
                        <span className="ml-2 text-[10px] text-amber-300/60">{when(m.created_at)}</span>
                      </div>
                    );
                    const mine = m.from === "staff";
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "rounded-br-md bg-venka-blue text-white" : "rounded-bl-md bg-slate-800/70 text-slate-100"}`}>
                          <p className={`mb-0.5 text-[11px] font-semibold ${mine ? "text-white/80" : "text-venka-green"}`}>{m.author_name}</p>
                          <p className="whitespace-pre-wrap break-words">{m.body}</p>
                          <p className={`mt-0.5 text-right text-[10px] ${mine ? "text-white/60" : "text-slate-500"}`}>{when(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                <div className="border-t border-slate-800 p-3">
                  <div className="mb-2 flex gap-1">
                    {pill(mode === "reply", () => setMode("reply"), tx.reply, "support-mode-reply")}
                    {pill(mode === "note", () => setMode("note"), tx.note, "support-mode-note")}
                    <span className="ml-auto hidden self-center text-[11px] text-slate-500 sm:inline">{tx.shortcut}</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} data-testid="support-input"
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                      placeholder={mode === "note" ? tx.notePh : tx.replyPh}
                      className={`min-h-[72px] w-full resize-none rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${mode === "note" ? "border-amber-400/40 bg-amber-400/5 text-slate-100 focus:ring-amber-400/30" : "border-slate-700 bg-slate-950 text-slate-100 focus:ring-venka-blue/30"}`} />
                    <Button onClick={send} disabled={busy || !text.trim()} className={`h-10 rounded-xl ${mode === "note" ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "bg-venka-blue text-white hover:bg-venka-blue-dark"}`} data-testid="support-send">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "note" ? <StickyNote className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                      <span className="ml-1.5 hidden sm:inline">{mode === "note" ? tx.addNote : tx.send}</span>
                    </Button>
                  </div>
                </div>
              </div>

              <aside className="hidden space-y-4 overflow-y-auto border-l border-slate-800 p-4 text-sm xl:block">
                <div className="space-y-2">
                  <DeptChip d={conv.department} tx={tx} />
                  <p className="flex items-center gap-2 text-slate-300"><Mail className="h-3.5 w-3.5 text-slate-500" /><a href={`mailto:${conv.email}`} className="truncate hover:underline">{conv.email}</a></p>
                  {conv.phone && <p className="flex items-center gap-2 text-slate-300"><Phone className="h-3.5 w-3.5 text-slate-500" /><a href={`tel:${conv.phone}`}>{conv.phone}</a></p>}
                  {conv.page && <p className="flex items-center gap-2 text-slate-400"><Globe2 className="h-3.5 w-3.5 text-slate-500" />{tx.from} <code className="truncate text-xs">{conv.page}</code></p>}
                  <p className="text-xs text-slate-500">{new Date(conv.created_at).toLocaleString()} · {(conv.lang || "").toUpperCase()}</p>
                </div>
                <div className="border-t border-slate-800 pt-3">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{tx.customer}</p>
                  {detail.customer ? <p className="font-medium text-slate-200">{detail.customer.name}</p> : <p className="text-xs text-slate-500">{tx.noAccount}</p>}
                </div>
                {detail.cases?.length > 0 && (
                  <div className="border-t border-slate-800 pt-3">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{tx.cases}</p>
                    <div className="space-y-1.5">
                      {detail.cases.map((p) => (
                        <Link key={p.id} to={`/portal/processes/${p.id}`} className="flex items-center gap-2 rounded-lg border border-slate-800 px-2.5 py-1.5 text-xs text-slate-300 hover:border-venka-blue">
                          <FolderKanban className="h-3.5 w-3.5 text-venka-blue" /><span className="truncate">{p.reference}</span><span className="ml-auto text-slate-500">{p.country_destination}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalSupport;
